import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Spinner,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import xhr from 'xhr';
import EnrollStudent from '../instituciones/components/EnrollStudent';
import SchoolPaymentsTab from './components/SchoolPaymentsTab';

export default function SchoolHome() {
  const toast = useToast();

  const [loading, setLoading] = React.useState(true);
  const [schoolId, setSchoolId] = React.useState(null);
  const [school, setSchool] = React.useState(null);
  const [stats, setStats] = React.useState({ students: 0, payments: 0 });

  React.useEffect(() => {
    const init = async () => {
      try {
        // Resolve school from current user
        const userRes = await xhr('/users/me?populate=school');
        const userSchool = userRes.data?.school;

        if (!userSchool?.id) {
          toast({
            title: 'No se encontro una institucion vinculada a tu cuenta',
            status: 'error',
            position: 'bottom',
            duration: 5000,
            isClosable: true,
          });
          setLoading(false);
          return;
        }

        setSchoolId(userSchool.id);

        // Fetch school details
        const schoolRes = await xhr(`/schools/${userSchool.id}?populate=*`);
        const data = schoolRes.data.data;
        const attrs = data.attributes || data;
        setSchool(attrs);

        // Fetch stats
        const [studentsRes, paymentsRes] = await Promise.all([
          xhr(
            `/students?filters[schoolFrom][id][$eq]=${userSchool.id}&pagination[pageSize]=1&pagination[withCount]=true`
          ),
          xhr(
            `/payment-schools?filters[school][id][$eq]=${userSchool.id}&pagination[pageSize]=1&pagination[withCount]=true`
          ),
        ]);
        setStats({
          students: studentsRes.data.meta?.pagination?.total || 0,
          payments: paymentsRes.data.meta?.pagination?.total || 0,
        });
      } catch (error) {
        console.error('Error loading school:', error);
        toast({
          title: 'Error al cargar datos de la institucion',
          status: 'error',
          position: 'bottom',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (!school || !schoolId) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.500">No se encontro una institucion vinculada a tu cuenta.</Text>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <Flex alignItems="center" gap={3} mb={6}>
        <Heading size="md">{school.name}</Heading>
        <Badge colorScheme={school.active ? 'green' : 'red'} ml={2}>
          {school.active ? 'Activo' : 'Inactivo'}
        </Badge>
      </Flex>

      <Tabs colorScheme="purple" isLazy>
        <TabList>
          <Tab>Informacion</Tab>
          <Tab>Estudiantes</Tab>
          <Tab>Pagos</Tab>
        </TabList>

        <TabPanels>
          {/* Tab: Informacion (solo lectura) */}
          <TabPanel>
            <Flex gap={6} flexWrap="wrap">
              <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" flex="1" minW="300px">
                <Heading size="sm" mb={4}>
                  Datos de la Institucion
                </Heading>
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Tbody>
                      <Tr>
                        <Td fontWeight="semibold" color="gray.600" border="none">Nombre</Td>
                        <Td border="none">{school.name}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold" color="gray.600" border="none">Subdominio</Td>
                        <Td border="none">{school.domain || '-'}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold" color="gray.600" border="none">Contacto</Td>
                        <Td border="none">{school.contact_name || '-'}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold" color="gray.600" border="none">Email</Td>
                        <Td border="none">{school.contact_email || '-'}</Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="semibold" color="gray.600" border="none">Telefono</Td>
                        <Td border="none">{school.cellphone || '-'}</Td>
                      </Tr>
                      {school.country && (
                        <Tr>
                          <Td fontWeight="semibold" color="gray.600" border="none">Pais</Td>
                          <Td border="none">{school.country}</Td>
                        </Tr>
                      )}
                      {school.city && (
                        <Tr>
                          <Td fontWeight="semibold" color="gray.600" border="none">Ciudad</Td>
                          <Td border="none">{school.city}</Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>

              <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" minW="250px">
                <Heading size="sm" mb={4}>
                  Resumen
                </Heading>
                <StatGroup flexDirection="column" gap={4}>
                  <Stat>
                    <StatLabel>Estudiantes Registrados</StatLabel>
                    <StatNumber>{stats.students}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Pagos Registrados</StatLabel>
                    <StatNumber>{stats.payments}</StatNumber>
                  </Stat>
                </StatGroup>
              </Box>
            </Flex>
          </TabPanel>

          {/* Tab: Estudiantes */}
          <TabPanel>
            <EnrollStudent schoolId={schoolId} />
          </TabPanel>

          {/* Tab: Pagos */}
          <TabPanel>
            <SchoolPaymentsTab schoolId={schoolId} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
